import { useState } from 'react';

import { useDispatch } from 'react-redux';

import { useAppSelector } from 'src/app/store/hooks';
import { DeleteModal, DeleteModalProps } from 'src/common/DeleteModal';
import { useAssignmentsBreaksDestroyMutation } from 'src/common/external/bambi-api/bambiApi';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { useDispatchRepository } from '../common/useDispatchRepository';
import {
  dismissDispatchModal,
  removeBreak,
  useDispatchModalToShow,
} from '../dispatcher.slice';

export const DeleteBreakModal = () => {
  const props = useDeleteBreakModalProps();
  return <DeleteModal {...props} />;
};

// Extracted hook for testability since modals don't render in React root
export const useDeleteBreakModalProps = (): DeleteModalProps => {
  const modalToShow = useDispatchModalToShow();
  const { error, onDeleteAssignmentBreak, dismissModal, loading } =
    useDeleteBreakModal();

  return {
    open: modalToShow === 'deleteBreak',
    setOpen: (isOpen) => {
      if (!isOpen) {
        dismissModal();
      }
    },
    onCancel: () => dismissModal(),
    onConfirm: () => {
      onDeleteAssignmentBreak();
    },
    title: 'Delete this break?',
    description:
      'Are you sure you want to delete this break? This action cannot be undone.',
    error,
    loading,
  };
};

const useDeleteBreakModal = () => {
  const [error, setError] = useState<string | undefined>(undefined);

  const dispatch = useDispatch();

  const assignmentBreak = useAppSelector(
    (state) => state.dispatcher.modals.assignmentBreak
  );
  const { refetch } = useDispatchRepository();
  const [deleteBreak, destroyState] = useAssignmentsBreaksDestroyMutation();

  const dismissModal = () => {
    setError(undefined);
    dispatch(dismissDispatchModal());
  };

  const onDeleteAssignmentBreak = async () => {
    if (!assignmentBreak?.id) {
      throw new Error('Missing assignment break ID');
    }

    try {
      await deleteBreak({
        id: assignmentBreak.assignment_id,
        breakId: assignmentBreak.id,
      }).unwrap();

      // Find break in state and remove it to prevent a slow network from
      // allowing the user to attempt to delete it again
      dispatch(removeBreak(assignmentBreak.id));

      refetch();

      dismissModal();
    } catch (error) {
      console.warn('error', error);
      const formattedError = formatServerError(error);
      setError(formattedError);
    }
  };

  return {
    error,
    onDeleteAssignmentBreak,
    dismissModal,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    loading: destroyState?.isLoading,
  };
};
